// The vendor panel's Wise recipient (bills and payments): which recipient in HHL's Wise profile gets this vendor's USD
// payments. The bank details stay in Wise; HHL Ops keeps the recipient's id, its name and the last 4 of the account.
// Find it by the name or the last digits of the account; linking (and unlinking) takes "Can pay".
import { Landmark, Search } from "lucide-react";
import { useState } from "react";
import { postJson, useCanPay, wiseFeature } from "../../lib/bills.ts";
import { ago } from "../../lib/format.ts";
import { mutate } from "../../lib/mutate.ts";
import { Link } from "../../lib/router.tsx";
import { useRows } from "../../lib/sync.ts";
import { Button, Input } from "../../ui/controls.tsx";
import { Section } from "../../ui/RecordPanel.tsx";
import { toast } from "../../ui/toast.tsx";

type Found = { id: number; name: string; currency: string; country: string; type: string; last4: string; vendor_id: string | null };

export function VendorWise({ vendorId }: { vendorId: string }) {
  const linked = useRows("vendor_recipient").filter((r) => r.vendor_id === vendorId);
  const vendors = useRows("vendor");
  const canPay = useCanPay();
  const wise = wiseFeature();
  const [q, setQ] = useState("");
  const [found, setFound] = useState<Found[] | null>(null);
  const [busy, setBusy] = useState(false);
  const search = async () => {
    setBusy(true);
    try {
      const res = await fetch(`/api/wise/recipients?q=${encodeURIComponent(q)}`);
      const body = (await res.json()) as { recipients?: Found[]; error?: string };
      if (!res.ok) throw new Error(body.error ?? "Couldn't search Wise");
      setFound(body.recipients ?? []);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const link = async (r: Found) => {
    try {
      await postJson("/api/wise/recipients/link", { vendor_id: vendorId, recipient_id: r.id });
      toast.ok(`Linked to ${r.name} in Wise`);
      setFound(null);
      setQ("");
    } catch (e) {
      toast.error((e as Error).message);
    }
  };
  return (
    <Section title="Wise recipient">
      <div className="px-2 pb-2 text-[12px]">
        {linked.length ? (
          linked.map((r) => (
            <div key={r.id} className="flex items-center gap-2 rounded-md border border-line px-2.5 py-1.5">
              <Landmark className="size-3.5 text-faint" />
              <span className="min-w-0 flex-1 truncate">
                <span className="font-medium">{r.name}</span>
                <span className="text-muted">
                  {r.last4 ? ` ···· ${r.last4}` : ""} · {r.currency}
                  {r.country ? ` · ${r.country}` : ""} · Wise #{r.wise_recipient_id} · linked {ago(r.updated_at)}
                </span>
              </span>
              {canPay && (
                <button
                  type="button"
                  className="text-faint hover:text-danger"
                  onClick={() => confirm("Forget this Wise recipient? (Wise keeps it)") && mutate("vendor_recipient.remove", { id: r.id })}
                >
                  Unlink
                </button>
              )}
            </div>
          ))
        ) : (
          <div className="text-muted">
            Not linked: Pay with Wise needs the vendor's recipient in HHL's Wise profile (add the recipient in Wise first if it isn't there).
          </div>
        )}
        {!wise.enabled ? (
          <div className="mt-1.5 text-faint">
            Wise isn't connected here:{" "}
            <Link to="/settings/wise" className="underline">
              Settings → Wise
            </Link>
          </div>
        ) : canPay ? (
          <div className="mt-2">
            <div className="flex gap-1.5">
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && search()}
                placeholder={linked.length ? "Change it: search Wise by name or account digits" : "Search Wise by name or the account's last digits"}
                className="h-7"
              />
              <Button size="sm" icon={<Search className="size-3.5" />} disabled={busy} onClick={search}>
                Find
              </Button>
            </div>
            {found && (
              <div className="mt-1.5 overflow-hidden rounded-md border border-line">
                {!found.length && <div className="px-2.5 py-2 text-muted">No USD recipient matches in Wise.</div>}
                {found.map((r) => {
                  const other = r.vendor_id && r.vendor_id !== vendorId ? vendors.find((v) => v.id === r.vendor_id) : undefined;
                  return (
                    <div key={r.id} className="flex items-center gap-2 border-t border-line px-2.5 py-1.5 first:border-t-0">
                      <span className="min-w-0 flex-1 truncate">
                        <span className="font-medium">{r.name}</span>
                        <span className="text-muted">
                          {r.last4 ? ` ···· ${r.last4}` : ""} · {r.currency}
                          {r.country ? ` · ${r.country}` : ""}
                        </span>
                        {other && <span className="text-warn"> · linked to {other.short_name || other.name}</span>}
                      </span>
                      <Button size="sm" onClick={() => link(r)} disabled={r.vendor_id === vendorId}>
                        {r.vendor_id === vendorId ? "Linked" : "Link"}
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          <div className="mt-1.5 text-faint">Linking a recipient takes Can pay (it decides where money goes).</div>
        )}
      </div>
    </Section>
  );
}
